<template>
    <div class="certificate-view flex flex-column">
        <v-container class="p-0 flex-grow-1 d-flex flex-column" fluid>
            <div
                class="page-card"
                data-aos="fade-left"
                data-aos-duration="1500"
            >
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        data-aos="fade-up"
                        data-aos-duration="1500"
                    >
                        <h1 class="card-title mb-3">
                            {{ $t('admin.certificates.settingsTitle') }}
                        </h1>
                        <div>
                            <Loader v-show="isLoading" />
                            <canvas
                                v-show="!isLoading"
                                ref="certificateCanvas"
                                :width="canvas.width"
                                :height="canvas.height"
                                class="certificate-canvas"
                            ></canvas>
                        </div>
                        <div class="d-none d-md-flex justify-content-end mt-3">
                            <v-btn
                                variant="elevated"
                                color="primary"
                                size="large"
                                width="166"
                                border="2"
                                @click="saveAll()"
                                :disabled="isLoading"
                            >
                                {{ $t('common.save') }}
                            </v-btn>
                        </div>
                    </v-col>
                    <v-col
                        cols="12"
                        md="4"
                        data-aos="fade-down"
                        data-aos-duration="1500"
                    >
                        <div class="tabs-card">
                            <v-tabs
                                v-model="tab"
                                grow
                                color="#1F1E61"
                                class="custom-tabs"
                            >
                                <v-tab value="templates">{{ $t('admin.certificates.templatesTab') }}</v-tab>
                                <v-tab value="content">{{ $t('admin.certificates.contentTab') }}</v-tab>
                            </v-tabs>

                            <v-window v-model="tab">
                                <v-window-item value="templates">
                                    <SelectTemplate
                                        :templates="templates"
                                        :selected="selectedTemplate?.type"
                                        @template-selected="
                                            changeSelectedTemplate
                                        "
                                    />
                                </v-window-item>

                                <v-window-item value="content">
                                    <FormContent
                                        :selected-logo="selectedLogo"
                                        :work-load-enabled="workload.enabled"
                                        :conclusion-date-enabled="
                                            conclusionDate.enabled
                                        "
                                        :selected-signature="signature"
                                        @logo-selected="onChangeSelectedLogo"
                                        @toggle-work-load="onToggleWorkLoad"
                                        @toggle-conclusion-date="
                                            onToggleConclusionDate
                                        "
                                        @toggle-signature="onToggleSignature"
                                        @signature-name-changed="
                                            onChangeSignatureName
                                        "
                                        @signature-selected="
                                            onChangeSignatureImg
                                        "
                                    />
                                </v-window-item>
                            </v-window>
                        </div>
                    </v-col>
                    <v-col class="p-4 d-md-none">
                        <v-btn
                            variant="elevated"
                            color="primary"
                            size="large"
                            width="100%"
                            border="2"
                            @click="saveAll()"
                            :disabled="isLoading"
                        >
                            {{ $t('common.save') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </v-container>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';
    import apiAWS from '@/utils/storage/aws';
    import apiCertificate from '@/utils/certificate';

    import Loader from '@/components/Loader/loader.vue';
    import SelectTemplate from '@/components/Admin/Certificate/SelectTemplate.vue';
    import FormContent from '@/components/Admin/Certificate/FormContent.vue';

    import {
        certificateTemplates,
        canvasWidth,
        canvasHeight,
        logoWidth,
        logoHeight,
        signatureWidth,
        signatureHeight,
    } from '@/mocks/certificateTemplates.js';

    export default {
        name: 'Certificate',
        components: {
            Loader,
            SelectTemplate,
            FormContent,
        },
        data() {
            return {
                tab: 'templates',
                isLoading: false,
                canvas: {
                    ref: null,
                    width: canvasWidth,
                    height: canvasHeight,
                },
                templates: certificateTemplates,
                certificateId: null,
                selectedTemplate: null,
                studentName: '',
                courseName: '',
                selectedLogo: {
                    file: null,
                    finalPath: '',
                    width: logoWidth,
                    height: logoHeight,
                    changed: false,
                },
                workload: {
                    enabled: true,
                    text: '',
                },
                conclusionDate: {
                    enabled: true,
                    text: '',
                },
                signature: {
                    enabled: true,
                    text: '',
                    file: null,
                    finalPath: '',
                    width: signatureWidth,
                    height: signatureHeight,
                    changed: false,
                },
            };
        },
        computed: {
            ...mapState(['token']),
        },
        mounted() {
            this.initCertificatePreviewText();
            this.loadCanvas();
            this.loadCertificateData();
        },
        methods: {
            initCertificatePreviewText() {
                this.studentName = this.$t('admin.certificates.previewStudentName');
                this.courseName = this.$t('admin.certificates.previewCourseName');
                this.workload.text = this.$t('admin.certificates.previewWorkloadHours');
                this.conclusionDate.text = this.$t('admin.certificates.previewConclusionDate');
                this.signature.text = this.$t('admin.certificates.previewSignature');
            },
            loadCanvas() {
                this.canvas.ref = this.$refs.certificateCanvas;
            },
            changeSelectedTemplate(template) {
                if (!template || !template.background) return;
                if (this.selectedTemplate === template) return;
                this.selectedTemplate = template;
                this.renderCertificate();
            },
            onChangeSelectedLogo(file) {
                this.selectedLogo.finalPath = '';
                this.selectedLogo.file = file;
                this.selectedLogo.changed = true;
                this.renderCertificate();
            },
            onToggleWorkLoad(enabled) {
                this.workload.enabled = enabled;
                this.renderCertificate();
            },
            onToggleConclusionDate(enabled) {
                this.conclusionDate.enabled = enabled;
                this.renderCertificate();
            },
            onToggleSignature(enabled) {
                this.signature.enabled = enabled;
                this.renderCertificate();
            },
            onChangeSignatureName(event) {
                this.signature.text = event;
                this.renderCertificate();
            },
            onChangeSignatureImg(file) {
                this.signature.finalPath = '';
                this.signature.file = file;
                this.signature.changed = true;
                this.renderCertificate();
            },
            renderCertificate() {
                this.isLoading = true;
                const canvas = this.canvas.ref;
                if (!canvas || !this.selectedTemplate) return;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // 1. Desenhar background
                const bgImg = new window.Image();
                bgImg.onload = () => {
                    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

                    // 2. Escrever nome do estudante
                    this.writeOnCanvas(
                        ctx,
                        this.studentName,
                        this.selectedTemplate.studentNamePosition.x,
                        this.selectedTemplate.studentNamePosition.y,
                        this.selectedTemplate.fontStudentName.family,
                        this.selectedTemplate.fontStudentName.size,
                        this.selectedTemplate.fontStudentName.weight ??
                            undefined,
                        this.selectedTemplate.fontStudentName.color
                    );

                    //2.1. Escrever nome do curso
                    const courseName = this.$t('admin.certificates.previewCompletedCourse', { name: this.courseName });
                    this.writeOnCanvas(
                        ctx,
                        courseName,
                        this.selectedTemplate.workLoadPosition.x,
                        this.selectedTemplate.workLoadPosition.y,
                        this.selectedTemplate.fontWorkLoad.family,
                        this.selectedTemplate.fontWorkLoad.size,
                        this.selectedTemplate.fontWorkLoad.weight ?? undefined,
                        this.selectedTemplate.fontWorkLoad.color
                    );

                    // 2.2. Escrever carga horária (se habilitado)
                    if (this.workload.enabled) {
                        const worLoadText =
                            this.$t('admin.certificates.previewWithWorkload', { hours: this.workload.text });
                        this.writeOnCanvas(
                            ctx,
                            worLoadText,
                            this.selectedTemplate.workLoadPosition.x,
                            this.selectedTemplate.workLoadPosition.y + 44,
                            this.selectedTemplate.fontWorkLoad.family,
                            this.selectedTemplate.fontWorkLoad.size,
                            this.selectedTemplate.fontWorkLoad.weight ??
                                undefined,
                            this.selectedTemplate.fontWorkLoad.color
                        );
                    }

                    // 2.3. Escrever data de conclusão (se habilitado)
                    if (this.conclusionDate.enabled) {
                        this.writeOnCanvas(
                            ctx,
                            this.conclusionDate.text,
                            this.selectedTemplate.conclusionDatePosition.x,
                            this.selectedTemplate.conclusionDatePosition.y,
                            this.selectedTemplate.fontConclusionDate.family,
                            this.selectedTemplate.fontConclusionDate.size,
                            this.selectedTemplate.fontConclusionDate.weight ??
                                undefined,
                            this.selectedTemplate.fontConclusionDate.color
                        );
                    }
                    // 3. Desenhar logo (se houver)
                    const logoImage = this.selectedLogo.finalPath
                        ? this.selectedLogo.finalPath
                        : this.selectedLogo.file;
                    if (logoImage) {
                        this.drawnOnCanvas(
                            ctx,
                            logoImage,
                            this.selectedTemplate.logoPosition.x,
                            this.selectedTemplate.logoPosition.y,
                            this.selectedLogo.width,
                            this.selectedLogo.height
                        );
                    }

                    // 4. Desenhar assinatura (se houver)
                    const signatureImage = this.signature.finalPath
                        ? this.signature.finalPath
                        : this.signature.file;
                    if (this.signature.enabled && signatureImage) {
                        this.drawnOnCanvas(
                            ctx,
                            signatureImage,
                            this.selectedTemplate.signaturePosition.x,
                            this.selectedTemplate.signaturePosition.y,
                            this.signature.width,
                            this.signature.height
                        );
                    } else if (this.signature.enabled && this.signature.text) {
                        this.writeOnCanvas(
                            ctx,
                            this.signature.text,
                            canvas.width / 2,
                            this.selectedTemplate.signaturePosition.y,
                            this.selectedTemplate.fontSignature.family,
                            this.selectedTemplate.fontSignature.size,
                            this.selectedTemplate.fontSignature.weight ??
                                undefined,
                            this.selectedTemplate.fontSignature.color
                        );
                    }

                    this.isLoading = false;
                };
                bgImg.src = this.selectedTemplate.background;
            },

            drawnOnCanvas(ctx, file, x, y, width, height) {
                if (!ctx || !file) return;
                const img = new window.Image();

                img.onload = () => {
                    ctx.drawImage(img, x, y, width, height);
                    // Libera o objeto URL se for File/Blob
                    if (file instanceof File || file instanceof Blob) {
                        URL.revokeObjectURL(img.src);
                    }
                };

                if (typeof file === 'string') {
                    img.src = file;
                } else if (file instanceof File || file instanceof Blob) {
                    img.src = URL.createObjectURL(file);
                } else {
                    // Não tenta desenhar se não for válido
                    return;
                }
            },
            writeOnCanvas(
                ctx,
                text,
                x,
                y,
                fontFamily = 'Inter',
                fontSize = 20,
                fontWeight = 'normal',
                color = '#000000',
                textAlign = 'center'
            ) {
                if (!ctx) return;
                ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
                ctx.fillStyle = color;
                ctx.textAlign = textAlign;
                ctx.fillText(text, x, y);
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                });
            },
            async loadCertificateData() {
                this.isLoading = true;
                try {
                    const response = await apiCertificate.getAllCertificates(
                        this.token
                    );
                    const certificateData = response.find(
                        (template) => template.status === 1
                    );

                    if (certificateData) {
                        this.certificateId = certificateData.id;

                        // Set selected template by type index
                        const foundTemplate = this.templates.find(
                            (tpl) => tpl.type === certificateData.type
                        );
                        if (foundTemplate) {
                            this.selectedTemplate = foundTemplate;
                        }

                        // Set logo if exists
                        if (certificateData.logoImage) {
                            this.selectedLogo.file = certificateData.logoImage;
                            this.selectedLogo.finalPath =
                                certificateData.logoImageFinal;
                        }

                        // Set signature image or text
                        if (certificateData.signatureImage) {
                            this.signature.file =
                                certificateData.signatureImage;
                            this.signature.finalPath =
                                certificateData.signatureImageFinal;
                        }
                        if (certificateData.signatureName) {
                            this.signature.text = certificateData.signatureName;
                        }

                        // Set toggles
                        this.workload.enabled =
                            !!certificateData.showCourseHours;
                        this.conclusionDate.enabled =
                            !!certificateData.showCompletionDate;
                        this.signature.enabled =
                            !!certificateData.showSignature;

                        // Render certificate with loaded data
                        this.$nextTick(() => {
                            this.renderCertificate();
                        });
                    } else {
                        if (this.templates && this.templates.length > 0) {
                            this.selectedTemplate = this.templates[0];
                            this.renderCertificate();
                        }
                    }
                } catch (e) {
                    toast.error(this.$t('admin.certificates.loadError'));
                } finally {
                    this.isLoading = false;
                }
            },
            async saveAll() {
                this.isLoading = true;
                try {
                    let logoImage;
                    let signatureImage;

                    if (this.selectedLogo.file && this.selectedLogo.changed) {
                        logoImage = await this.saveImageAws(
                            this.selectedLogo.file
                        );
                    } else {
                        logoImage = this.selectedLogo.file;
                    }

                    if (this.signature.file && this.signature.changed) {
                        signatureImage = await this.saveImageAws(
                            this.signature.file
                        );
                    } else {
                        signatureImage = this.signature.file;
                    }

                    const payload = {
                        status: 1,
                        description: this.selectedTemplate?.name || '',
                        type: this.selectedTemplate?.type,
                        showCourseHours: this.workload.enabled ? 1 : 0,
                        showCompletionDate: this.conclusionDate.enabled ? 1 : 0,
                        showSignature: this.signature.enabled ? 1 : 0,
                        signatureName: this.signature.text || '',
                        signatureImage: signatureImage || '',
                        logoImage: logoImage || '',
                    };

                    if (this.certificateId) {
                        // Atualiza o certificado existente (PUT)
                        await apiCertificate.editCertificate(
                            this.token,
                            this.certificateId,
                            payload
                        );
                        toast.success(this.$t('admin.certificates.updateSuccess'));
                    } else {
                        // Cria um novo certificado (POST)
                        await apiCertificate.createNewCertificate(
                            this.token,
                            payload
                        );
                        toast.success(this.$t('admin.certificates.saveSuccess'));
                    }
                } catch (e) {
                    toast.error(this.$t('admin.certificates.saveError'));
                } finally {
                    this.isLoading = false;
                }
            },
            async putFileAws(url, file) {
                try {
                    const response = await apiAWS.putFileAWS(url, file);
                    return response;
                } catch (error) {
                    this.notify(this.$t('admin.certificates.imageSaveError'), 'error');
                }
            },
            async saveImageAws(file) {
                try {
                    const response = await apiAWS.getSignatureImage(
                        this.token,
                        file.name
                    );
                    await this.putFileAws(response.url, file);
                    return response.token;
                } catch (error) {
                    this.notify(this.$t('admin.certificates.imageTokenError'), 'error');
                }
            },
        },
    };
</script>

<style scoped>
    svg {
        width: auto;
        max-width: 100%;
    }

    .certificate-view {
        text-align: start;
        color: #3a3a3a;
    }

    .tabs-card {
        width: 100%;
        height: 80svh;
        overflow: hidden;
        background-color: #ffffff;
        border: 1px solid #d6d8de;
        border-radius: 10px;
        box-shadow: 0 2px 16px 4px rgba(85, 96, 139, 0.09);
    }

    .default-btn {
        display: block;
        padding: 12px 24px;
        border: 1px solid transparent;
        border-radius: 4px;
        font-size: 0.9375rem;
        font-weight: 600;
        transition: all 0.3s ease-out;
    }

    .purple-btn {
        font-weight: 700;
        background-color: #1f1e61;
        color: #e3e7fb;
    }

    .purple-btn:hover {
        color: #1f1e61;
        background-color: #e3e7fb;
        border-color: #1f1e61;
    }

    .certificate-canvas {
        width: 100%;
        height: auto;
        border: 1px solid #d6d8de;
        border-radius: 6px;
        background-color: #f5f5f5;
    }

    .w-40 {
        width: 160px;
    }

    .dark .certificate-view {
        color: #ffffff;
    }

    .dark .tabs-card {
        background-color: #2c2c2c;
        border: 1px solid #444444;
        box-shadow: 0 2px 16px 4px rgba(0, 0, 0, 0.1);
    }

    .dark .purple-btn {
        background-color: #3a3a3a;
        color: #e3e7fb;
    }
    .dark .purple-btn:hover {
        color: #3a3a3a;
        background-color: #e3e7fb;
        border-color: #3a3a3a;
    }

    .dark .custom-tabs button {
        color: #ffffff !important;
        caret-color: #ffffff !important;
    }
</style>
